import React, { useCallback } from 'react';
import { CardPaymentDialog } from 'pages/Registration/components/CardPaymentDialog';
import { CashPaymentDialog } from 'pages/Registration/components/CashPaymentDialog';
import { DeliveryDialog } from 'pages/Registration/components/DeliveryDialog';
import { FinishRegistrationDialog } from 'components/FinishRegistrationDialog/FinishRegistrationDialog';
import { makeStyles } from '@material-ui/core/styles';
import { makeVendorMenuHideAction } from 'state/vendorMenu/actions';
import { MarketingMaterials } from 'pages/MarketingMaterials/MarketingMaterials';
import { OnboardingScreen } from '../Onboarding/types';
import { onboardingScreenSelector } from '../../state/onboarding/selectors';
import { OnlinePaymentDialog } from 'pages/Registration/components/OnlinePaymentDialog';
import { OrderActionType } from 'state/order/actions';
import { orderNewCountSelector } from 'state/order/selector';
import { Orders } from '../Orders/Orders';
import { PickUpDialog } from 'pages/Registration/components/PickUpDialog';
import { Route, useHistory } from 'react-router-dom';
import { RoutePath } from 'RoutePath';
import { useAction, useActionDispatchOnMount, useActivate } from 'core/store/store';
import { useSelector } from 'react-redux';
import { VendorBanner } from 'pages/Vendor/VendorBanner/VendorBanner';
import { VendorCatalog } from './Catalog/VendorCatalog';
import { VendorDashboard } from 'pages/Vendor/VendorDashboard/VendorDashboard';
import { VendorFooter } from './VendorFooter/VendorFooter';
import { VendorHeader } from './VendorHeader/VendorHeader';
import { VendorMenuDialog } from './VendorMenu/VendorMenuDialog';
import { vendorMenuIsShownSelector } from 'state/vendorMenu/selector';
import { VendorProfile } from './VendorProfile/VendorProfile';
import { VendorStoreInfo } from './VendorStoreInfo/VendorStoreInfo';
import { VendorSubscription } from './VendorSubscription/VendorSubscription';
import { VendorPromo } from './VendorPromo/VendorPromo';
import { makeUserMenuProfileFetchAction } from 'state/userMenu/actions';
import {
    subscriptionBannerSelector,
    subscriptionCurrentSelector,
    subscriptionPaymentMethodSelector,
} from '../../state/subscription/selectors';
import { numDaysBetween } from '../../core/date/date';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
    },
    header: {
        position: 'sticky',
        top: 0,
        backgroundColor: '#fafafa',
        zIndex: 2,
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    vendorMenu: {
        height: '100%',
        [theme.breakpoints.down('xs')]: {
            paddingBottom: 56,
        },
    },
    footer: {
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
        position: 'fixed',
        bottom: 0,
        width: '100%',
        zIndex: 20,
    },
}));

export const Vendor = () => {
    useActionDispatchOnMount(true, makeUserMenuProfileFetchAction);
    const showMenuDialog = useSelector(vendorMenuIsShownSelector);
    useActivate(OrderActionType.ACTIVATE, OrderActionType.DEACTIVATE);
    const newOrderCount = useSelector(orderNewCountSelector);
    const hasNewOrders = newOrderCount > 0;
    const history = useHistory();
    const action = useAction();

    const screen = useSelector(onboardingScreenSelector);

    const classes = useStyles();

    const closeMenuAction = useCallback(() => {
        action(makeVendorMenuHideAction);
    }, []);

    const currentSubscription = useSelector(subscriptionCurrentSelector);
    const paymentMethod = useSelector(subscriptionPaymentMethodSelector);
    const hideBanner = useSelector(subscriptionBannerSelector);
    const showActivateSub = !currentSubscription?.active;
    const subDays = Math.round(numDaysBetween(new Date(currentSubscription?.renewalDate), new Date()));
    const renewDateInFuture = new Date(currentSubscription?.renewalDate) >= new Date();
    const renewSub = !currentSubscription?.active && renewDateInFuture;
    const lastSubDay = subDays < 2 && !currentSubscription?.active;

    const isBannerHidden =
        (subDays && subDays > 14 && renewDateInFuture) ||
        hideBanner ||
        (!lastSubDay && !showActivateSub && !renewSub) ||
        (paymentMethod?.cardLast4 && !showActivateSub);

    return (
        <div className={classes.container}>
            {screen === OnboardingScreen.SUCCESS && <FinishRegistrationDialog />}
            {!isBannerHidden && (
                <VendorBanner
                    subDays={subDays}
                    renewDateInFuture={renewDateInFuture}
                    renewSub={renewSub}
                    lastSubDay={lastSubDay}
                />
            )}
            <div className={classes.header}>
                <VendorHeader hasNewOrders={hasNewOrders} />
            </div>
            {showMenuDialog && (
                <VendorMenuDialog onClose={closeMenuAction}>
                    <footer className={classes.footer}>
                        <VendorFooter hasNewOrders={hasNewOrders} />
                    </footer>
                </VendorMenuDialog>
            )}
            <Route path={RoutePath.VENDOR_DASHBOARD} exact={true}>
                <VendorDashboard />
            </Route>
            <Route path={RoutePath.VENDOR_CATALOG}>
                <VendorCatalog />
            </Route>
            <Route path={RoutePath.ORDERS}>
                <Orders />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_PROFILE} exact={true}>
                <VendorProfile />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_STORE_INFO}>
                <VendorStoreInfo isBannerHidden={isBannerHidden} />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_SUBSCRIPTION} exact={true}>
                <VendorSubscription />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_SETTINGS_DELIVERY} exact={true}>
                <DeliveryDialog onClose={() => history.push(RoutePath.VENDOR_MENU_STORE_INFO)} />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_SETTINGS_PICKUP} exact={true}>
                <PickUpDialog onClose={() => history.push(RoutePath.VENDOR_MENU_STORE_INFO)} />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_SETTINGS_CASH} exact={true}>
                <CashPaymentDialog onClose={() => history.push(RoutePath.VENDOR_MENU_STORE_INFO)} />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_SETTINGS_CARD} exact={true}>
                <CardPaymentDialog onClose={() => history.push(RoutePath.VENDOR_MENU_STORE_INFO)} />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_SETTINGS_ONLINE} exact={true}>
                <OnlinePaymentDialog onClose={() => history.push(RoutePath.VENDOR_MENU_STORE_INFO)} />
            </Route>
            <Route path={RoutePath.VENDOR_MENU_MARKETING_MATERIALS} exact={true}>
                <MarketingMaterials onClose={() => history.push(RoutePath.VENDOR_MENU_STORE_INFO)} />
            </Route>
            <Route path={RoutePath.VENDOR_PROMO}>
                <VendorPromo />
            </Route>
            <footer className={classes.footer}>
                <VendorFooter hasNewOrders={hasNewOrders} />
            </footer>
        </div>
    );
};
